import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Marker, GeoJSON, useMap, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import PropTypes from 'prop-types';
import { Typography, Box, Divider, IconButton, Collapse, Paper } from '@mui/material';
import { 
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  LocationOnOutlined as LocationOnOutlinedIcon,
  LocationOn as LocationOnIcon,
  SchoolOutlined as SchoolOutlinedIcon,
  School as SchoolIcon
} from '@mui/icons-material';

// Import map utilities
import { 
  customIcon, 
  getSchoolColor,
  driveTimeColors,
  colorScales,
} from '../../utils/unifiedMapUtils';

import {
  calculateMarketShares,
  filterSchools,
  deduplicateSchools,
} from '../../utils/schoolUtils';

// MapContainer remounts whenever mapKey changes (it includes travel mode), and
// MapContainer removes the Leaflet instance on unmount. That can happen between
// scheduling an animation frame and running it. Leaflet drops its panes in
// remove(), and calling fitBounds without them throws "Cannot set properties of
// undefined (setting '_leaflet_pos')", so check for a pane before touching a map
// asynchronously.
const isMapUsable = (map) => Boolean(map && map.getPane('mapPane'));

const Legend = React.memo(({ selectedSchool, mode }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 20,
        right: 10,
        backgroundColor: 'white',
        borderRadius: '5px',
        zIndex: 1000,
        boxShadow: '0 0 10px rgba(0,0,0,0.1)',
        maxWidth: '200px'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1 }}>
        <Typography variant="subtitle2">Legend</Typography>
        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ p: 1 }}>
          {selectedSchool && (
            <Box display="flex" alignItems="center" mb={1}>
              {selectedSchool.charter === 'Yes' ? (
                <SchoolIcon sx={{ mr: 1, color: '#4338ca' }} />
              ) : (
                <LocationOnIcon sx={{ mr: 1, color: '#4338ca' }} />
              )}
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {selectedSchool.name}
              </Typography>
            </Box>
          )}
          <Box display="flex" alignItems="center" mb={1}>
            <SchoolOutlinedIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2">Charter School</Typography>
          </Box>
          <Box display="flex" alignItems="center" mb={1}>
            <LocationOnOutlinedIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2">District School</Typography>
          </Box>
          <Box display="flex" alignItems="center" mb={1}>
            <Box 
              sx={{ 
                mr: 1, 
                width: 16, 
                height: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" style={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                <path fill="currentColor" d="M12 2L22 12L12 22L2 12L12 2Z" stroke="currentColor" strokeWidth="1" fillOpacity="0.8"/>
              </svg>
            </Box>
            <Typography variant="body2">Private School</Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          {colorScales[mode].map(({ color, label }) => (
            <Box key={label} display="flex" alignItems="center" mb={1}>
              {color.startsWith('linear-gradient') ? (
                <Box 
                  width={100} 
                  height={20} 
                  mr={1} 
                  sx={{ 
                    background: color,
                    borderRadius: '4px'
                  }} 
                />
              ) : (
                <Box 
                  width={20} 
                  height={20} 
                  bgcolor={color} 
                  mr={1} 
                />
              )}
              <Typography variant="body2">{label}</Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
});

const MapOverlay = React.memo(({ position, children }) => {
  const map = useMap();
  const [point, setPoint] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!map || !position) return;

    const updatePosition = () => {
      const latLng = L.latLng(position[0], position[1]);
      const point = map.latLngToContainerPoint(latLng);
      setPoint(point);
    };

    updatePosition();

    map.on('zoom', updatePosition);
    map.on('move', updatePosition);
    map.on('moveend', updatePosition);

    return () => {
      map.off('zoom', updatePosition);
      map.off('move', updatePosition);
      map.off('moveend', updatePosition);
    };
  }, [map, position]);

  if (!position) return null;

  return (
    <div style={{
      position: 'absolute',
      left: point.x,
      top: point.y,
      transform: 'translate(-50%, -50%)',
      pointerEvents: 'none',
      zIndex: 900
    }}>
      {children}
    </div>
  );
});

const InfoPanel = React.memo(({ 
  school, 
  data, 
  onClose, 
  PopupComponent, 
  schoolsEnrollmentData,
  selectedGrades,
  mode
}) => {

  if (!school) return null;

  // Find the full school data
  const fullSchoolData = schoolsEnrollmentData.find(d => d.uuid === school.uuid);

  // Prepare props based on mode
  const popupProps = mode === 'market-share' ? {
    school,
    marketShareData: data?.marketShareData,
    enrollmentData: data?.enrollmentData,
    selectedGrades
  } : {
    school,
    schoolData: fullSchoolData,
    selectedGrades
  };

  return (
    <Paper
      sx={{
        position: 'absolute',
        left: 10,
        top: 10,
        zIndex: 1100,
        padding: 1.5,
        width: {
          xs: '90%',
          sm: 330,
          md: 330,
        },
        maxWidth: 500,
        maxHeight: 'calc(100% - 20px)',
        overflowY: 'auto',
        overflowX: 'hidden',
        backgroundColor: 'white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{ 
          position: 'absolute', 
          right: 8, 
          top: 8,
          zIndex: 1200
        }}
      >
        <CloseIcon />
      </IconButton>
      <PopupComponent key={`popup-${school.uuid}`} {...popupProps} />
    </Paper>
  );
});

const SelectionIndicator = React.memo(() => (
  <div className="map-selection-indicator">
    <svg width="40" height="40" viewBox="0 0 40 40">
      <circle cx="20" cy="20" r="18" fill="none" stroke="#3388ff" strokeWidth="2" />
      <circle cx="20" cy="20" r="4" fill="#3388ff" />
    </svg>
  </div>
));

const MapContent = React.memo(({
  mode,
  selectedSchool,
  filteredSchools,
  marketShares: externalMarketShares,
  esriData,
  visibleDriveTimes,
  schoolsEnrollmentData,
  governanceFilter,
  selectedGrades,
  schoolTypeFilter,
  PopupComponent,
  focusedSchool,
  onSchoolSelect,
  initialZoom,
  districtBoundaries,
  showDistrictBoundaries
}) => {
  const map = useMap();
  const fitBoundsFrame = useRef(null);
  const [geoJsonData, setGeoJsonData] = useState({});
  const [selectedMarkerSchool, setSelectedMarkerSchool] = useState(null);
  const [selectionIndicatorPos, setSelectionIndicatorPos] = useState(null);
  const [nearbySchoolCount, setNearbySchoolCount] = useState(0);

  // Memoize marketShares calculation
  const marketShares = useMemo(() => {
    if (externalMarketShares) return externalMarketShares;
    if (mode !== 'market-share') return {};
    return calculateMarketShares(filteredSchools, schoolsEnrollmentData, selectedGrades);
  }, [mode, filteredSchools, schoolsEnrollmentData, selectedGrades, externalMarketShares]);

  // Process GeoJSON data
  const updateGeoJsonData = useCallback((data) => {
    if (!data?.drive_times || !isMapUsable(map)) {
      return;
    }
  
    const newGeoJsonData = {};
    // Fit to the selected drive time's polygon so zoom matches the visible ring
    const selectedTime = visibleDriveTimes[0] || 10;
    let selectedPolygonBounds = null;
    
    Object.entries(data.drive_times).forEach(([driveTime, timeData]) => {
      if (!timeData?.drive_time_polygon) return;
      
      try {
        let polygonData = timeData.drive_time_polygon;
        if (typeof polygonData === 'string') {
          polygonData = JSON.parse(polygonData);
          if (typeof polygonData === 'string') {
            polygonData = JSON.parse(polygonData);
          }
        }
        
        const coordinates = polygonData.rings;
        if (!coordinates?.length) return;
        
        const geoJsonFeature = {
          type: "Feature",
          properties: {},
          geometry: {
            type: "Polygon",
            coordinates: coordinates
          }
        };
  
        newGeoJsonData[parseInt(driveTime)] = geoJsonFeature;
        
        if (parseInt(driveTime) === selectedTime) {
          const bounds = L.geoJSON(geoJsonFeature).getBounds();
          if (bounds.isValid()) {
            selectedPolygonBounds = bounds;
          }
        }
      } catch (error) {
        console.error(`Error processing drive time ${driveTime}:`, error);
      }
    });
  
    setGeoJsonData(newGeoJsonData);
  
    if (selectedPolygonBounds) {
      cancelAnimationFrame(fitBoundsFrame.current);
      fitBoundsFrame.current = requestAnimationFrame(() => {
        // The map can be torn down between scheduling this frame and running it.
        if (isMapUsable(map)) {
          map.fitBounds(selectedPolygonBounds, {
            padding: [5, 5],
            animate: true
          });
        }
      });
    }
  }, [map, visibleDriveTimes]);

  useEffect(() => () => cancelAnimationFrame(fitBoundsFrame.current), []);
  
  useEffect(() => {
    if (map) {
      const originalFlyTo = map.flyTo;
      map.flyTo = function(...args) {
        if (selectedMarkerSchool) {
          return this;
        }
        return originalFlyTo.apply(this, args);
      };
      return () => {
        map.flyTo = originalFlyTo;
      };
    }
  }, [map, selectedMarkerSchool]);
  
  useEffect(() => {
    if (esriData) {
      updateGeoJsonData(esriData);
    }
  }, [esriData, updateGeoJsonData]);

  useEffect(() => {
    if (map) {
      const originalFlyTo = map.flyTo;
      map.flyTo = function(...args) {
        if (selectedMarkerSchool) {
          return this;
        }
        return originalFlyTo.apply(this, args);
      };
      return () => {
        map.flyTo = originalFlyTo;
      };
    }
  }, [map, selectedMarkerSchool]);

  useEffect(() => {
    if (esriData) {
      updateGeoJsonData(esriData);
    }
  }, [esriData, updateGeoJsonData]);

  // Handle marker clicks with stable reference
  const handleMarkerClick = useCallback((school) => {
    if (!map) return;
    
    setSelectedMarkerSchool(current => 
      current?.uuid === school.uuid ? null : school
    );
    setSelectionIndicatorPos(current => 
      current ? null : [school.latitude, school.longitude]
    );
    
    onSchoolSelect?.(school.uuid);
  }, [map, onSchoolSelect]);

  const handleInfoPanelClose = useCallback(() => {
    setSelectedMarkerSchool(null);
    setSelectionIndicatorPos(null);
    // Don't call map.setView at all
    onSchoolSelect?.(null);
  }, [onSchoolSelect]);

  // Calculate visible schools with proper memoization
  const visibleSchools = useMemo(() => {    
    const filters = {
      selectedGrades,
      governanceFilter,
      schoolTypeFilter,
    };
    
    const filtered = filterSchools(filteredSchools, filters, schoolsEnrollmentData);
    
    const uniqueSchools = deduplicateSchools(filtered);
    
    if (!uniqueSchools.find(school => school.uuid === selectedSchool.uuid)) {
      uniqueSchools.unshift(selectedSchool);
    }
    
    const nearbyCount = uniqueSchools.filter(school => 
      visibleDriveTimes.includes(school.drive_time) || school.uuid === selectedSchool.uuid
    ).length;
    setNearbySchoolCount(nearbyCount);
  
    return uniqueSchools;
  }, [
    filteredSchools, 
    selectedGrades, 
    governanceFilter, 
    schoolTypeFilter, 
    schoolsEnrollmentData, 
    selectedSchool,
    visibleDriveTimes
  ]);

  // Memoize markers
  const memoizedMarkers = useMemo(() => 
    visibleSchools
      .filter(school => 
        school?.latitude && 
        school?.longitude &&
        (visibleDriveTimes.includes(school.drive_time) || school.uuid === selectedSchool.uuid)
      )
      .map(school => {
        const isSelected = school.uuid === selectedSchool.uuid;
        const isFocused = school.uuid === focusedSchool?.uuid;
        let finalColor;
        let opacity = 1;
  
        if (isSelected) {
          finalColor = '#4338ca'; // Selected school color
        } else if (isFocused) {
          finalColor = '#2196f3'; // Focused school color
        } else {
          // Use the mode parameter to determine coloring strategy
          finalColor = getSchoolColor(school, schoolsEnrollmentData, mode, marketShares, selectedGrades);
          if (focusedSchool) {
            opacity = 0.3;
          }
        }
  
        return (
          <Marker 
            key={school.uuid}
            position={[school.latitude, school.longitude]} 
            icon={customIcon({ 
              color: finalColor, 
              isSelected: isSelected || isFocused,
              isCharter: school.charter === 'Yes',
              isPrivate: school.is_private,
              opacity 
            })}
            eventHandlers={{
              click: (e) => {
                e.originalEvent.stopPropagation();
                handleMarkerClick(school);
              }
            }}
          />
        );
      }),
    [
      visibleSchools,
      visibleDriveTimes,
      selectedSchool,
      focusedSchool,
      schoolsEnrollmentData,
      marketShares,
      handleMarkerClick,
      mode,
      selectedGrades  // Add selectedGrades to dependencies
    ]
  );

  // Memoize GeoJSON layers
  const memoizedGeoJSON = useMemo(() => {
    return Object.entries(geoJsonData)
      .map(([driveTime, data]) => {
        const isVisible = visibleDriveTimes.includes(parseInt(driveTime));
        const color = driveTimeColors[parseInt(driveTime)] || '#4338ca';
        
        if (!isVisible || !data) return null;

        return (
          <GeoJSON 
            key={driveTime}
            data={data} 
            style={() => ({
              color: color,
              weight: 2,
              opacity: 1,
              fillColor: color,
              fillOpacity: 0.1,
              zIndex: 400
            })}
            eventHandlers={{
              add: (e) => {}  // Empty handler since logs are removed
            }}
            ref={(layerRef) => {}}  // Empty ref callback since logs are removed
          />
        );
      })
      .filter(Boolean);
  }, [geoJsonData, visibleDriveTimes]);

  // Memoize District Boundaries layers
  // Generate distinct colors for districts based on name
  const getDistrictColor = (districtName) => {
    // Predefined color palette for districts
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
      '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B88B', '#ABEBC6',
      '#FAD7A0', '#D7BDE2', '#A9CCE3', '#F9E79F', '#A3E4D7',
      '#E6B0AA', '#D5DBDB', '#AED6F1', '#F5CBA7', '#A9DFBF'
    ];
    
    // Simple hash function to consistently assign colors
    let hash = 0;
    for (let i = 0; i < districtName.length; i++) {
      hash = districtName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const memoizedDistrictBoundaries = useMemo(() => {
    if (!showDistrictBoundaries || !districtBoundaries?.features) {
      return null;
    }

    const onEachFeature = (feature, layer) => {
      const districtName = feature.properties.NAME || 'Unknown District';
      const baseColor = getDistrictColor(districtName);
      
      // Store the default style
      const defaultStyle = {
        color: '#555',
        weight: 2,
        opacity: 0.6,
        dashArray: '8, 4',
        fillOpacity: 0
      };

      // Explicitly unbind any popups
      layer.unbindPopup();
      
      // Bind tooltip - only shows on hover
      layer.bindTooltip(districtName, {
        permanent: false,
        sticky: false,
        direction: 'center',
        className: 'district-tooltip',
        opacity: 0.9,
        interactive: false  // Tooltip itself is not interactive
      });

      // Disable ALL click-related interactions to prevent bounding box
      layer.on('click', (e) => {
        L.DomEvent.stop(e);
        // Immediately close any tooltip that might have opened
        layer.closeTooltip();
        // Also close all tooltips on the map
        if (map) {
          map.closePopup();
          map.eachLayer((l) => {
            if (l.closeTooltip) {
              l.closeTooltip();
            }
          });
        }
      });

      layer.on('mousedown', (e) => {
        L.DomEvent.stop(e);
        layer.closeTooltip();
      });

      layer.on('mouseup', (e) => {
        L.DomEvent.stop(e);
      });

      layer.on('dblclick', (e) => {
        L.DomEvent.stop(e);
      });

      // Disable pointer events for clicking but keep for hovering
      if (layer._path) {
        layer._path.style.pointerEvents = 'visiblePainted';
        layer._path.style.cursor = 'default';
        // Add a class to target with CSS if needed
        layer._path.classList.add('district-boundary-no-select');
      }

      // Completely disable selection
      layer.options.clickable = false;
      layer.options.keyboard = false;
      layer.options.noWrap = true;

      // Add hover effects - show color on hover only
      layer.on('mouseover', (e) => {
        if (layer._path) {
          layer._path.style.cursor = 'default';
        }
        layer.setStyle({
          fillColor: baseColor,
          fillOpacity: 0.3,  // Show color on hover
          weight: 3,
          opacity: 0.8
        });
        // Explicitly open tooltip only on hover
        layer.openTooltip(e.latlng);
      });

      layer.on('mouseout', () => {
        // Always reset to default style
        layer.setStyle(defaultStyle);
        // Explicitly close tooltip
        layer.closeTooltip();
      });

      // Prevent tooltip from opening on any event other than mouseover
      layer.on('tooltipopen', (e) => {
        // If tooltip opens from anything other than hover, close it immediately
        const isFromHover = layer._path && layer._path.matches(':hover');
        if (!isFromHover) {
          setTimeout(() => {
            layer.closeTooltip();
          }, 0);
        }
      });
    };

    const getStyle = () => {
      return {
        color: '#555',
        weight: 2,
        opacity: 0.6,
        dashArray: '8, 4',  // Dashed lines
        fillOpacity: 0,  // No fill by default
        zIndex: 300,
        className: 'district-boundary-layer'  // Custom class for styling
      };
    };

    return (
      <GeoJSON
        key={`district-boundaries-${selectedSchool?.uuid || 'default'}-${districtBoundaries?.features?.length || 0}`}
        data={districtBoundaries}
        style={getStyle}
        onEachFeature={onEachFeature}
        interactive={true}
        bubblingMouseEvents={false}
        pmIgnore={true}
        pane="overlayPane"
      />
    );
  }, [districtBoundaries, showDistrictBoundaries, selectedSchool, map]);


  // Initialize map
  useEffect(() => {
    if (!map) return;
    
    const handleMapReady = () => {
      map.scrollWheelZoom.disable();
      
      const handleScroll = (e) => {
        if (e.ctrlKey) {
          map.scrollWheelZoom.enable();
        } else {
          map.scrollWheelZoom.disable();
        }
      };
  
      map.on('preclick', () => {
        map.scrollWheelZoom.enable();
      });
      
      map.on('mouseout', () => {
        map.scrollWheelZoom.disable();
      });

      // Close all tooltips when clicking on the map
      map.on('click', () => {
        map.eachLayer((layer) => {
          if (layer.closeTooltip) {
            layer.closeTooltip();
          }
          if (layer.closePopup) {
            layer.closePopup();
          }
        });
      });
  
      document.addEventListener('keydown', handleScroll);
      document.addEventListener('keyup', handleScroll);
  
      return () => {
        document.removeEventListener('keydown', handleScroll);
        document.removeEventListener('keyup', handleScroll);
        map.off('preclick');
        map.off('mouseout');
        map.off('click');
      };
    };

    if (map._loaded) {
      handleMapReady();
    } else {
      map.once('load', handleMapReady);
    }

    return () => {
      if (map) {
        map.off('load');
      }
    };
  }, [map]);

  return (
    <>
      <TileLayer
        url="https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png"
        attribution='&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>'
      />
      {memoizedDistrictBoundaries}
      {memoizedGeoJSON}
      {memoizedMarkers}
      
      {/* School count */}
      {!selectedMarkerSchool && (
        <div 
          style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: '8px 12px',
            borderRadius: '4px',
            zIndex: 1000,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <Typography variant="body2">
            Total Schools: {nearbySchoolCount}
          </Typography>
        </div>
      )}

      {/* Legend */}
      <Legend 
        selectedSchool={selectedSchool}
        mode={mode}
      />

      {/* Info Panel */}
      {/* Inside MapContent return statement */}
      {selectedMarkerSchool && (
        <>
          <InfoPanel 
            school={selectedMarkerSchool}
            data={mode === 'market-share' ? {
              // A null "2019-20" share is the deliberate answer for a school with no
              // comparable baseline, and the popup renders it as such. It must stay null
              // rather than being defaulted, or the popup reports today's whole share as
              // growth since then.
              marketShareData: {
                "2024-25": marketShares[selectedMarkerSchool.uuid]?.marketData?.current,
                "2019-20": marketShares[selectedMarkerSchool.uuid]?.marketData?.comparison ?? null,
                rank: marketShares[selectedMarkerSchool.uuid]?.marketData?.rank,
                enrollments: {
                  "2024-25": {
                    school: marketShares[selectedMarkerSchool.uuid]?.marketData?.currentEnrollment,
                    total: marketShares[selectedMarkerSchool.uuid]?.marketData?.currentTotalEnrollment
                  },
                  "2019-20": {
                    school: marketShares[selectedMarkerSchool.uuid]?.marketData?.pastEnrollment,
                    total: marketShares[selectedMarkerSchool.uuid]?.marketData?.pastTotalEnrollment
                  }
                }
              },
              enrollmentData: schoolsEnrollmentData.find(d => d.uuid === selectedMarkerSchool.uuid),
            } : null}
            mode={mode}
            schoolsEnrollmentData={schoolsEnrollmentData}
            onClose={handleInfoPanelClose}
            PopupComponent={PopupComponent}
            selectedGrades={selectedGrades}
          />
        </>
      )}

      {/* Selection Indicator */}
      {selectionIndicatorPos && (
        <MapOverlay position={selectionIndicatorPos}>
          <SelectionIndicator />
        </MapOverlay>
      )}
    </>
  );
});

const UnifiedSchoolMap = React.memo(({ 
  mode = 'geographic',
  selectedSchool,
  filteredSchools,
  marketShares,
  esriData = null,
  visibleDriveTimes = [],
  schoolsEnrollmentData,
  governanceFilter = 'All',
  selectedGrades = [],
  schoolTypeFilter = 'All',
  PopupComponent,
  focusedSchool = null,
  onSchoolSelect,
  initialZoom = 15,
  onMapLoaded,
  districtBoundaries = null,
  showDistrictBoundaries = true
}) => {

  const renderCount = useRef(0);

  useEffect(() => {
    renderCount.current += 1;
  });

  // Include travel_mode in key so MapContainer fully remounts when mode changes,
  // guaranteeing GeoJSON layers are mounted with fresh data (not stale cache).
  const travelMode = esriData?.travel_mode || 'drive';
  const mapKey = useMemo(() => 
    `${selectedSchool?.uuid}-${visibleDriveTimes.join('-')}-${travelMode}`,
    [selectedSchool?.uuid, visibleDriveTimes, travelMode]
  );

  // Stable callback reference
  const handleMapReady = useCallback(() => {
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
      if (onMapLoaded) {
        onMapLoaded();
      }
    });
  }, [onMapLoaded]);

  if (!selectedSchool) {
    return <Typography>Please select a school to view the map.</Typography>;
  }

  return (
    <div 
      className="relative w-full h-full" 
      style={{ 
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      <MapContainer 
        key={mapKey}
        center={[selectedSchool.latitude, selectedSchool.longitude]}
        zoom={initialZoom}  // Use prop for initial zoom
        maxZoom={16}  // Allow zooming in quite far
        minZoom={8}   // Allow zooming out quite far
        style={{ 
          position: 'absolute',
          height: '100%', 
          width: '100%',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0
        }}
        zoomControl={false}
        whenReady={(e) => {
          handleMapReady(e);
        }}
        whenCreated={(map) => {}}
      >
        <ZoomControl position="topright" />
        <MapContent 
          mode={mode}
          selectedSchool={selectedSchool}
          filteredSchools={filteredSchools}
          esriData={esriData}
          visibleDriveTimes={visibleDriveTimes}
          schoolsEnrollmentData={schoolsEnrollmentData}
          marketShares={marketShares}
          governanceFilter={governanceFilter}
          selectedGrades={selectedGrades}
          schoolTypeFilter={schoolTypeFilter}
          PopupComponent={PopupComponent}
          focusedSchool={focusedSchool}
          onSchoolSelect={onSchoolSelect}
          initialZoom={initialZoom}
          districtBoundaries={districtBoundaries}
          showDistrictBoundaries={showDistrictBoundaries}
        />
      </MapContainer>

      <div 
        style={{
          position: 'absolute',
          bottom: '20px',
          left: '20px',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          padding: '4px 12px',
          borderRadius: '4px',
          zIndex: 1000,
          maxWidth: '200px',
          pointerEvents: 'none'
        }}
      >
        <Typography variant="caption">
          Hold Ctrl to zoom with scroll wheel, or click to enable zoom
        </Typography>
      </div>
    </div>
  );
});

UnifiedSchoolMap.propTypes = {
  mode: PropTypes.oneOf(['geographic', 'market-share']),
  selectedSchool: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
    charter: PropTypes.string.isRequired,
  }).isRequired,
  filteredSchools: PropTypes.arrayOf(PropTypes.object).isRequired,
  marketShares: PropTypes.object,
  esriData: PropTypes.object,
  visibleDriveTimes: PropTypes.arrayOf(PropTypes.number),
  schoolsEnrollmentData: PropTypes.arrayOf(PropTypes.object).isRequired,
  governanceFilter: PropTypes.arrayOf(PropTypes.string),
  selectedGrades: PropTypes.arrayOf(PropTypes.string),
  schoolTypeFilter: PropTypes.arrayOf(PropTypes.string),
  PopupComponent: PropTypes.elementType.isRequired,
  focusedSchool: PropTypes.object,
  onSchoolSelect: PropTypes.func.isRequired,
  initialZoom: PropTypes.number,
  onMapLoaded: PropTypes.func,
  districtBoundaries: PropTypes.object,
  showDistrictBoundaries: PropTypes.bool
};

export default UnifiedSchoolMap;
